<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { Porthole } from '@client/components/porthole';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { GroupsService } from '@client/services/groups';

  import { generatePastelColors } from '@common/utils/colors';

  export let config = {};
  export let field = '';
  export let onChange = () => {};
  export let readOnly = null;

  let selected = [];
  let selectedItems = [];
  let items = [];
  let originalItems = [];
  let previousFilterText = '';
  let filterText = '';
  let searching = false;
  let loading = false;

  const colorAssignments = {};

  const group = config[field];

  const endpoints = {
    o365groupbindings: 'getOfficeGroups',
    googlegroupbindings: 'getGoogleGroups',
    oktagroupbindings: 'getOktaGroups'
  };

  const setGroupColor = (value, colors) => {
    if (!colorAssignments[value]) {
      colorAssignments[value] = colors.pop();
    }

    return colorAssignments[value];
  };

  const processGroupItem = (group, colors, selected) => {
    const { directMembersCount, name, uuid, ...rest } = group;

    const item = {
      ...rest,
      name,
      id: uuid,
      label: name,
      color: setGroupColor(uuid, colors),
      total: directMembersCount,
      value: uuid,
      type: field,
      checked: selected.includes(uuid)
    };

    if (item.checked && !selectedItems.some(({ id }) => id === item.id)) {
      selectedItems.push(item);

      selectedItems = [...selectedItems];
    }

    return item;
  };

  const fetchAndProcessGroups = async (query, isSearch) => {
    const groups = await GroupsService[endpoints[field]]({ bindings: group, query });
    const list = Array.isArray(groups) ? groups : [];

    const colors = generatePastelColors(list.length);

    const processedGroups = list.map((group) => processGroupItem(group, colors, selected)).filter(Boolean);

    if (isSearch) {
      processedGroups.forEach((item) => {
        if (!originalItems.some((existingItem) => existingItem.id === item.id)) {
          originalItems.push(item);
        }
      });
    } else {
      originalItems = processedGroups.reduce((uniqueItems, item) => {
        if (!uniqueItems.some((existingItem) => existingItem.id === item.id)) {
          uniqueItems.push(item);
        }

        return uniqueItems;
      }, []);
    }

    originalItems = [...originalItems].sort((a, b) => a.name.localeCompare(b.name));

    items = [...originalItems];
  };

  const getGroups = async ({ value = '' }) => {
    loading = true;

    selected = (group || []).map(({ id, employee_id }) => id || employee_id);

    await fetchAndProcessGroups(value);

    loading = false;
  };

  const searchGroups = async () => {
    if (!filterText.length) {
      return;
    }

    if (!searching && previousFilterText !== filterText) {
      searching = true;

      previousFilterText = filterText;

      await fetchAndProcessGroups(filterText, true);

      searching = false;
    }
  };

  onMount(async () => await getGroups({ value: '' }));

  const onClear = ({ detail }) => {
    if (detail?.length) {
      selected = [];
    } else {
      selected = selected?.filter(({ value }) => value !== detail.value);
    }

    onSelect({ detail: selected || [] });
  };

  const onSelect = ({ detail = {} }) => {
    onChange(detail, field);
  };

  $: if (!filterText.length) {
    items = [...originalItems];
  }
</script>

{#if readOnly}
  {selectedItems.map(({ label, total }) => `${label} (${total})`).join(', ')}
{:else if loading}
  <div class="loading">
    <Spinner show inline />
  </div>
{:else}
  <Select
    bind:items
    bind:value={selected}
    on:change={onSelect}
    on:clear={onClear}
    on:filter={searchGroups}
    bind:filterText
    placeholder={$format('label.INCLUDED.GROUPS_PLACEHOLDER')}
    class="select-element"
    searchable
    multiple
  >
    <svelte:fragment slot="item" let:item>
      <div class="item">
        <Porthole details={item} color={item.color} />
        <div class="item-detail">
          <div class="name">{item.name}</div>
          <div class="email">{$format('label.MEMBERS_TOTAL', { values: { total: item.total } })}</div>
        </div>
      </div>
    </svelte:fragment>
  </Select>
{/if}

<style lang="scss" src="./group-selector.scss"></style>
